<template>
  <v-app>

    <!-- ===================== MODAL ===================== -->
    <v-dialog
      v-model="localDialog"
      max-width="500px"
      persistent
    >
      <v-card>
        <v-card-title class="text-h6 bg-blue-grey-lighten-4">
          <span>{{ isEntryMode ? "Add Size" : "Update Size" }}</span>
        </v-card-title>

        <v-card-text>
          <v-form @submit.prevent="submit" ref="formRef">
            <v-row>

              <!-- Name -->
              <v-col cols="12">
                <v-text-field
                  v-model="name.value.value"
                  label="Size Name"
                  variant="outlined"
                  density="compact"
                  :error-messages="name.errorMessage.value"
                  clearable
                >
                  <template #label>
                    Name <span class="text-red-accent-4">*</span>
                  </template>
                </v-text-field>
              </v-col>

              <!-- Status (Checkbox) -->
              <v-col cols="12">
                <v-checkbox
                  label="Active"
                  v-model="status.value.value"
                  :error-messages="status.errorMessage.value"
                  density="compact"
                  variant="outlined"
                  hide-details
                  single-line
                  color="primary"
                  :true-value="1"
                  :false-value="0"
                ></v-checkbox>
              </v-col>

              <input type="hidden" v-model="size_id.value.value" />

            </v-row>

            <v-divider></v-divider>
            <v-card-actions class="justify-end">
              <v-btn variant="flat" color="red" @click="closeDialog">Close</v-btn>
              <v-btn class="ms-2" variant="flat" color="primary" type="submit">
                {{ isEntryMode ? "Save" : "Update" }}
              </v-btn>
            </v-card-actions>
          </v-form>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- ===================== PAGE CONTENT ===================== -->
    <v-container fluid>
      <v-row>
        <v-col cols="12">

          <v-expansion-panels v-model="panel">
            <v-expansion-panel>
              <v-expansion-panel-title class="bg-blue-grey-lighten-4">
                Production Size List
              </v-expansion-panel-title>

              <v-expansion-panel-text>

                <!-- Search + Add Button -->
                <v-row class="py-2">
                  <v-col md="4" cols="12">
                    <v-text-field
                      v-model="search"
                      append-inner-icon="mdi-magnify"
                      label="Search"
                      variant="outlined"
                      density="compact"
                      hide-details
                      clearable
                    ></v-text-field>
                  </v-col>

                  <v-col md="3" offset-md="5" class="text-right">
                    <v-btn
                      color="primary"
                      variant="tonal"
                      prepend-icon="mdi-plus"
                      @click="openCreate"
                      rounded="xl"
                    >
                      Add Size
                    </v-btn>
                  </v-col>
                </v-row>

                <!-- DATA TABLE -->
                <v-data-table
                  :headers="headers"
                  :items="sizes"
                  :search="search"
                  :loading="loading"
                  density="compact"
                >
                  <template v-slot:item.index="{ index }">
                    {{ index + 1 }}
                  </template>

                  <template v-slot:item.status="{ item }">
                    <v-chip size="small" :color="item.status == 1 ? 'success' : 'red'">
                      {{ item.status == 1 ? "Active" : "Inactive" }}
                    </v-chip>
                  </template>

                  <template v-slot:item.actions="{ item }">
                    <v-icon color="primary" @click="openEdit(item)">mdi-pencil-outline</v-icon>
                  </template>
                </v-data-table>

              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>

        </v-col>
      </v-row>
    </v-container>

  </v-app>
</template>

<script>
import { ref } from "vue";
import { useField, useForm } from "vee-validate";
import * as yup from "yup";
import { useStore } from "vuex";
import { toast } from "vue3-toastify";

export default {
  data() {
    return {
      panel: [0],
      dialog: false,
      localDialog: false,
      loading: false,
      search: "",
      isEntryMode: true,
      headers: [
        { title: "#SL", key: "index", align: "center" },
        // { title: "Code", key: "code" },
        { title: "Name", key: "name" },
        { title: "Status", key: "status", align: "center" },
        { title: "Actions", key: "actions", align: "center", sortable: false },
      ],
    };
  },

  setup() {
    const store = useStore();

    const { handleSubmit, resetForm } = useForm({
      validationSchema: yup.object({
        name: yup.string().required().max(50).label("Name"),
        status: yup.boolean().required().label("Status"),
      }),
    });

    const name = useField("name");
    const status = useField("status");
    const size_id = useField("size_id");

    const localDialog = ref(false);

    const closeDialog = () => {
      localDialog.value = false;
      resetForm();
    };

    const submit = handleSubmit((values) => {
      const payload = {
        name: values.name,
        status: values.status ? 1 : 0, // boolean → number
      };

      const id = values.size_id;

      if (id) {
        store
          .dispatch("updateProductionSize", { id, data: payload })
          .then(() => {
            toast.success("Updated Successfully!");
            resetForm();
            closeDialog();
            store.dispatch("getProductionSizeList");
          })
          .catch((err) => {
            toast.error(err.response?.data?.message || "Something went wrong");
          });
      } else {
        store
          .dispatch("saveProductionSize", payload)
          .then(() => {
            toast.success("Created Successfully!");
            resetForm();
            closeDialog();
            store.dispatch("getProductionSizeList");
          })
          .catch((err) => {
            toast.error(err.response?.data?.message || "Something went wrong");
          });
      }
    });

    return {
      name,
      status,
      size_id,
      submit,
      resetForm,
      localDialog,
      closeDialog,
    };
  },

  computed: {
    sizes() {
      return this.$store.getters.getProductionSizeList;
    },
  },

  watch: {
    dialog(val) {
      this.localDialog = val;
    },
    localDialog(val) {
      this.dialog = val;
    },
  },

  methods: {
    loadList() {
      this.loading = true;
      this.$store.dispatch("getProductionSizeList").finally(() => {
        this.loading = false;
      });
    },

    openCreate() {
      this.isEntryMode = true;
      this.localDialog = true;
      this.resetForm();
      this.status.setValue(1); // default active
    },

    openEdit(item) {
      this.isEntryMode = false;
      this.localDialog = true;

      this.name.setValue(item.name || "");
      this.status.setValue(item.status == 1 ? 1 : 0); // Set to 1 or 0, not boolean
      this.size_id.setValue(item.id || "");
    },
  },

  created() {
    this.loadList();
  },
};
</script>
